import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardCheck, Loader2, PlayCircle, Clock, AlertTriangle, Search, ShieldCheck, ShieldAlert, ShieldOff, CircleHelp, Info, CalendarClock, TrendingDown, TrendingUp, UserCheck, UserX, Paperclip } from "lucide-react";
import { Switch } from "../ui/switch";
import { authApi } from "../../lib/auth";
import { errorMessage } from "../../lib/api";
import ScoreRing from "./ScoreRing";
import SectionBars, { STATUS_META } from "./SectionBars";
import ControlRow from "./ControlRow";
import AttestDialog from "./AttestDialog";
import ComplianceSetup from "./ComplianceSetup";
import ReportButtons from "./ReportButtons";
import ScoreTrend from "./ScoreTrend";
import AssignOwnerDialog from "./AssignOwnerDialog";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "");
const FILTERS = [
  ["all", "All"],
  ["fail", "Failing"],
  ["pass", "Passing"],
  ["manual", "Not assessed"],
  ["exception", "Accepted risk"],
  ["na", "N/A"],
  ["error", "Not evaluated"],
  ["overdue", "Reviews overdue"],
  ["mine", "Assigned to me"],
  ["unassigned", "Unassigned"],
  ["evidence", "With evidence"],
];
const OPEN = new Set(["fail", "manual", "error"]);
const matches = (c, k, me) => {
  if (k === "all") return true;
  if (k === "manual") return c.status === "manual" || c.status === "error";
  if (k === "overdue") return !!c.attestation?.overdue;
  if (k === "mine") return c.owner?.user_id === me;
  if (k === "unassigned") return OPEN.has(c.status) && !c.owner;
  if (k === "evidence") return (c.files || []).length > 0;
  return c.status === k;
};

/** CIS M365 v7 dashboard for one tenant: scan history, score, toggles, controls, attestations, reports. */
export default function ComplianceDashboard({ tenant, isAdmin }) {
  const tenantId = tenant.id;
  const isCsv = tenant.source === "csv";
  const [scans, setScans] = useState(null);
  const [scanId, setScanId] = useState("");
  const [view, setView] = useState(null);
  const [level, setLevel] = useState(1);
  const [e5, setE5] = useState("auto");
  const [unassessed, setUnassessed] = useState("exclude");
  const [filter, setFilter] = useState("all");
  const [section, setSection] = useState("");
  const [q, setQ] = useState("");
  const [starting, setStarting] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [ownerDialog, setOwnerDialog] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = searchParams.get("control") || "";
  const focusSetup = searchParams.get("setup") === "1";
  const timer = useRef(null);
  const params = useMemo(() => ({ level, e5, unassessed }), [level, e5, unassessed]);

  const loadScans = useCallback(async () => {
    const list = await authApi.compliance.scans(tenantId);
    setScans(list);
    return list;
  }, [tenantId]);

  useEffect(() => {
    setScanId("");
    setView(null);
    loadScans()
      .then((list) => {
        const latest = list.find((s) => s.status === "done") || list[0];
        if (latest) setScanId(latest.id);
      })
      .catch((err) => toast.error("Could not load CIS scans", { description: errorMessage(err) }));
  }, [loadScans]);

  const refresh = useCallback(async () => {
    if (!scanId) return;
    const v = await authApi.compliance.scan(scanId, params);
    setView(v);
    return v;
  }, [scanId, params]);

  useEffect(() => {
    if (!scanId) return undefined;
    let canceled = false;
    const poll = async () => {
      try {
        const v = await authApi.compliance.scan(scanId, params);
        if (canceled) return;
        setView(v);
        if (v.scan.status === "running") timer.current = setTimeout(poll, 2000);
        else {
          loadScans();
          if (v.scan.status === "error") toast.error("CIS scan failed", { description: v.scan.error });
        }
      } catch (err) {
        if (!canceled) toast.error("Could not load CIS scan", { description: errorMessage(err) });
      }
    };
    poll();
    return () => {
      canceled = true;
      clearTimeout(timer.current);
    };
  }, [scanId, params, loadScans]);

  const start = async () => {
    setStarting(true);
    try {
      const res = await authApi.compliance.start(tenantId);
      toast.success(res.already_running ? "A CIS scan is already running" : "CIS scan started");
      setView(null);
      setScanId(res.id);
      loadScans();
    } catch (err) {
      toast.error("Could not start the CIS scan", { description: errorMessage(err) });
    } finally {
      setStarting(false);
    }
  };

  useEffect(() => {
    if (!focusId || !view?.controls) return;
    const c = view.controls.find((x) => x.id === focusId);
    if (c && c.level === 2 && level === 1) setLevel(2);
    if (c && c.profile === "E5" && !view.score?.include_e5) setE5("true");
  }, [focusId, view, level]);

  const updateFiles = (controlId, files) =>
    setView((v) => {
      if (!v) return v;
      const controls = v.controls.map((c) => (c.id === controlId ? { ...c, files } : c));
      return { ...v, controls, score: v.score ? { ...v.score, with_evidence: controls.filter((c) => c.in_scope && c.files?.length).length } : v.score };
    });

  const clearAttestation = async (c) => {
    try {
      await authApi.compliance.clearAttestation(tenantId, c.id);
      toast.success("Cleared");
      refresh();
    } catch (err) {
      toast.error("Could not clear", { description: errorMessage(err) });
    }
  };

  const scan = view?.scan;
  const score = view?.score;
  const running = scan?.status === "running";
  const includeE5 = score ? score.include_e5 : scan?.profile === "E5";
  const controls = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (view?.controls || []).filter((c) => c.in_scope && matches(c, filter, view?.me) && (!section || c.section === section) && (!needle || c.id.startsWith(needle) || c.title.toLowerCase().includes(needle) || (c.evidence || "").toLowerCase().includes(needle) || (c.owner?.name || "").toLowerCase().includes(needle)));
  }, [view, filter, section, q]);
  const countFor = (k) => (view?.controls || []).filter((c) => c.in_scope && matches(c, k, view?.me)).length;
  const changes = scan?.changes;
  const delta = changes && !changes.first ? changes.delta : null;
  const slug = (tenant.default_domain || tenant.name || "tenant").toLowerCase().replace(/\s+/g, "-");

  if (isCsv) {
    return (
      <section className="ws-card glass" data-testid="cis-unavailable">
        <h2>
          <ShieldOff size={16} /> CIS compliance
        </h2>
        <div className="ws-alert">
          <Info size={14} /> This tenant was imported from a CSV export. CIS checks read tenant configuration (Conditional Access, Entra settings, SharePoint, Intune) from Microsoft Graph - link the tenant with an Entra app registration to scan it.
        </div>
      </section>
    );
  }

  return (
    <div className="ws-stack" data-testid="cis-dashboard">
      <div className="cis-toolbar glass">
        <div className="cis-toolbar-left">
          <ClipboardCheck size={18} className="cis-toolbar-ico" />
          <div>
            <b>CIS Microsoft 365 Foundations Benchmark v7.0.0</b>
            <small className="muted">
              {scan?.status === "done" ? `Scanned ${fmt(scan.finished_at)} · tenant profile ${scan.profile || "E3"}` : scans?.length === 0 ? "No CIS scan yet" : running ? "Scan in progress..." : ""}
            </small>
          </div>
        </div>
        <div className="cis-toolbar-right">
          {scan?.permissions?.missing?.length > 0 && (
            <button type="button" className="cis-perm-warning" onClick={() => setSearchParams({ setup: "1" }, { replace: true })} title={scan.permissions.missing.join(", ")} data-testid="cis-perm-warning">
              <AlertTriangle size={13} /> {scan.permissions.missing.length} Graph permission{scan.permissions.missing.length === 1 ? "" : "s"} missing
            </button>
          )}
          {scans?.length > 0 && (
            <label className="ws-select">
              <Clock size={13} />
              <select value={scanId} onChange={(e) => setScanId(e.target.value)} data-testid="cis-scan-select">
                {scans.map((s) => (
                  <option key={s.id} value={s.id}>
                    {`${fmt(s.started_at)} · ${s.status}${s.counts ? ` · ${s.counts.pass} pass / ${s.counts.fail} fail` : ""}`}
                  </option>
                ))}
              </select>
            </label>
          )}
          {isAdmin && (
            <button type="button" className="tb-btn feature cost" onClick={start} disabled={starting || running} data-testid="cis-run-btn">
              <span className="ico">{starting || running ? <Loader2 size={14} className="spin" /> : <PlayCircle size={14} />}</span>
              {running ? "Scanning..." : "Run CIS scan"}
            </button>
          )}
          {scan?.status === "done" && <ReportButtons scanId={scan.id} params={params} slug={slug} />}
        </div>
      </div>

      {scans?.length === 0 && !running && (
        <div className="ws-empty glass" data-testid="cis-empty">
          <ClipboardCheck size={28} />
          <h3>No CIS scan yet</h3>
          <p>{isAdmin ? "Run the first scan to evaluate the automated controls against this tenant's configuration." : "An admin needs to run the first CIS scan."}</p>
        </div>
      )}
      {running && (
        <div className="ws-empty glass" data-testid="cis-running">
          <Loader2 size={26} className="spin" />
          <h3>Evaluating CIS controls...</h3>
          <p>Reading Conditional Access, authorization and authentication-method policies, roles, PIM, device settings, SharePoint and Intune configuration, DNS records.</p>
        </div>
      )}
      {scan?.status === "error" && (
        <div className="ws-alert warn big" data-testid="cis-error">
          <AlertTriangle size={16} /> {scan.error}
        </div>
      )}

      {score && (
        <>
          <div className="cis-summary">
            <div className="ws-card glass cis-score-card" data-testid="cis-score-card">
              <ScoreRing percent={score.percent} size={120} label={`Level ${level}`} testId="cis-score" />
              <div className="cis-score-meta">
                <div className="kpi-value mono" data-testid="cis-score-text">
                  {score.percent === null ? "No assessed controls" : `${score.pass} of ${score.pass + score.fail} assessed controls pass`}
                </div>
                <div className="kpi-sub">
                  {score.in_scope} controls in scope · {score.exception} accepted risk · {score.na} N/A · {score.unassessed} not assessed{unassessed === "fail" ? " (counted as failing)" : " (excluded)"}
                </div>
                <div className="cis-kpis">
                  <span className="cis-kpi ok" data-testid="cis-kpi-pass">
                    <ShieldCheck size={13} /> {score.pass} pass
                  </span>
                  <span className="cis-kpi crit" data-testid="cis-kpi-fail">
                    <ShieldAlert size={13} /> {score.fail} fail
                  </span>
                  <span className="cis-kpi manual" data-testid="cis-kpi-unassessed">
                    <CircleHelp size={13} /> {score.unassessed} open
                  </span>
                  <span className="cis-kpi exception" data-testid="cis-kpi-exception">
                    <ShieldOff size={13} /> {score.exception} risk accepted
                  </span>
                  <button type="button" className={`cis-kpi ${score.overdue ? "crit" : ""} clickable`} onClick={() => setFilter(filter === "overdue" ? "all" : "overdue")} aria-pressed={filter === "overdue"} data-testid="cis-kpi-overdue">
                    <CalendarClock size={13} /> {score.overdue} review{score.overdue === 1 ? "" : "s"} overdue
                  </button>
                  <button type="button" className={`cis-kpi ${score.due_overdue ? "crit" : ""} clickable`} onClick={() => setFilter(filter === "mine" ? "all" : "mine")} aria-pressed={filter === "mine"} title="Controls with a remediation owner (click: assigned to me)" data-testid="cis-kpi-owners">
                    <UserCheck size={13} /> {score.assigned} owned{score.due_overdue ? ` · ${score.due_overdue} past due` : ""}
                  </button>
                  <button type="button" className="cis-kpi clickable" onClick={() => setFilter(filter === "unassigned" ? "all" : "unassigned")} aria-pressed={filter === "unassigned"} data-testid="cis-kpi-unassigned">
                    <UserX size={13} /> {score.open_unassigned} open unassigned
                  </button>
                  <button type="button" className="cis-kpi clickable" onClick={() => setFilter(filter === "evidence" ? "all" : "evidence")} aria-pressed={filter === "evidence"} data-testid="cis-kpi-evidence">
                    <Paperclip size={13} /> {score.with_evidence} with evidence
                  </button>
                </div>
                {delta !== null && delta !== undefined && (
                  <div className={`cis-delta ${delta < 0 ? "crit" : delta > 0 ? "ok" : ""}`} data-testid="cis-delta">
                    {delta < 0 ? <TrendingDown size={13} /> : <TrendingUp size={13} />}
                    {delta === 0 ? "No score change" : `${delta > 0 ? "+" : ""}${delta} pts`} vs previous scan ({changes.prev_percent}% → {changes.percent}%)
                    {changes.newly_failing?.length ? ` · ${changes.newly_failing.length} newly failing` : ""}
                    {changes.fixed?.length ? ` · ${changes.fixed.length} fixed` : ""}
                  </div>
                )}
              </div>
            </div>
            <div className="ws-card glass cis-options" data-testid="cis-options">
              <h2>Scope</h2>
              <div className="cis-option">
                <span>
                  <b>Profile level</b>
                  <small>Level 2 adds defense-in-depth controls on top of Level 1.</small>
                </span>
                <div className="seg" role="group" aria-label="Level">
                  <button type="button" className={level === 1 ? "on" : ""} onClick={() => setLevel(1)} aria-pressed={level === 1} data-testid="cis-level-1">
                    Level 1
                  </button>
                  <button type="button" className={level === 2 ? "on" : ""} onClick={() => setLevel(2)} aria-pressed={level === 2} data-testid="cis-level-2">
                    Level 1 + 2
                  </button>
                </div>
              </div>
              <label className="ws-switch cis-option" data-testid="cis-e5-toggle">
                <span>
                  <b>Include E5 controls</b>
                  <small>Defender, Purview, PIM, Identity Protection. {scan.profile === "E5" ? "Detected E5 licensing - on by default." : "No E5 licensing detected - off by default."}</small>
                </span>
                <Switch checked={!!includeE5} onCheckedChange={(v) => setE5(v ? "true" : "false")} />
              </label>
              <label className="ws-switch cis-option" data-testid="cis-unassessed-toggle">
                <span>
                  <b>Count unassessed as failing</b>
                  <small>Treat manual controls without an attestation (and controls that could not be evaluated) as Fail.</small>
                </span>
                <Switch checked={unassessed === "fail"} onCheckedChange={(v) => setUnassessed(v ? "fail" : "exclude")} />
              </label>
            </div>
          </div>

          <SectionBars sections={score.sections} active={section} onSelect={setSection} />
          <ScoreTrend tenantId={tenantId} e5={e5} refreshKey={`${scans?.length}-${scan.id}-${score.exception}-${score.pass}`} />
          <ComplianceSetup required={view.permissions_required || []} scan={scan} sample={tenant.sample} focus={focusSetup} />

          <section className="ws-card glass" data-testid="cis-controls-card">
            <div className="ws-card-head wrap">
              <h2>
                Controls <span className="count" data-testid="cis-controls-count">{controls.length}</span>
                {section && (
                  <button type="button" className="tb-btn sm ghost" onClick={() => setSection("")} data-testid="cis-section-clear">
                    Section {section} × 
                  </button>
                )}
              </h2>
              <div className="ws-filters">
                <div className="pills compact" role="group" aria-label="Filter controls">
                  {FILTERS.map(([k, label]) => (
                    <button key={k} type="button" className={`pill ${STATUS_META[k]?.cls || ""}`} aria-pressed={filter === k} onClick={() => setFilter(k)} data-testid={`cis-filter-${k}`}>
                      {label} <span className="count">{countFor(k)}</span>
                    </button>
                  ))}
                </div>
                <label className="ws-search">
                  <Search size={14} />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search id, title, evidence" data-testid="cis-search" />
                </label>
              </div>
            </div>
            {controls.length === 0 && <p className="muted" data-testid="cis-controls-empty">No controls match this filter.</p>}
            <ul className="cis-list" data-testid="cis-controls-list">
              {controls.map((c) => (
                <ControlRow key={c.id} control={c} isAdmin={isAdmin} me={view.me} tenantId={tenantId} focused={c.id === focusId} onAttest={(control, mode) => setDialog({ control, mode })} onClear={clearAttestation} onAssign={(control) => setOwnerDialog(control)} onFilesChange={updateFiles} />
              ))}
            </ul>
          </section>
        </>
      )}
      {dialog && (
        <AttestDialog
          tenantId={tenantId}
          control={dialog.control}
          mode={dialog.mode}
          isAdmin={isAdmin}
          onFilesChange={updateFiles}
          onClose={() => {
            setDialog(null);
            refresh();
          }}
          onSaved={() => {
            setDialog(null);
            refresh();
          }}
        />
      )}
      {ownerDialog && (
        <AssignOwnerDialog
          tenantId={tenantId}
          control={ownerDialog}
          onClose={() => setOwnerDialog(null)}
          onSaved={() => {
            setOwnerDialog(null);
            if (focusId) setSearchParams({}, { replace: true });
            refresh();
          }}
        />
      )}
    </div>
  );
}
